// ConfirmationDialog — generic confirmation pattern for destructive or
// state-changing actions per frontend-design §3 ConfirmationDialog.
//
// 409 race recovery (F-003):
// - When the parent passes `stateRaceMessage`, the dialog renders an
//   inline message above the actions and disables the confirm button;
//   the parent is responsible for triggering a refetch and auto-
//   dismissing the dialog on refresh-success.

import { useState } from 'react';
import { Modal } from './ModalHost';

interface Props {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmIntent: 'destructive' | 'transition';
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
  // If non-null, render the F-003 race banner instead of an error.
  stateRaceMessage?: string | null;
  // Generic per-attempt error to render inline. Cleared on each retry.
  errorMessage?: string | null;
}

export function ConfirmationDialog({
  open,
  title,
  body,
  confirmLabel,
  confirmIntent,
  onConfirm,
  onCancel,
  stateRaceMessage,
  errorMessage,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };
  const blockedByRace = !!stateRaceMessage;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      role="alertdialog"
      scrimDismissDisabled
      variant="modal"
      contentClassName="confirmation-dialog"
    >
      <div className="confirmation-body">{body}</div>
      {stateRaceMessage ? (
        <div
          className="inline-error inline-error-race"
          role="status"
          aria-live="polite"
        >
          {stateRaceMessage}
        </div>
      ) : null}
      {errorMessage && !blockedByRace ? (
        <div className="inline-error" role="alert">
          {errorMessage}
        </div>
      ) : null}
      <footer className="confirmation-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting && !blockedByRace}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`btn btn-${
            confirmIntent === 'destructive' ? 'destructive' : 'primary'
          }`}
          onClick={() => {
            void handleConfirm();
          }}
          disabled={submitting || blockedByRace}
          aria-busy={submitting ? 'true' : 'false'}
        >
          {submitting ? 'Working…' : confirmLabel}
        </button>
      </footer>
    </Modal>
  );
}
